import {
  ColDef,
  FilterChangedEvent,
  GridOptions,
  RowGroupOpenedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { UiStateKey } from '../../../../lib/commons/uiStates'
import { useBulkSheetState as useBulkSheetStateBase } from '../../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useCallback } from 'react'
import _ from 'lodash'

export const useBulkSheetState = (
  gridOptions: GridOptions,
  functionUuid: string,
  uiStateKey: UiStateKey
) => {
  const base = useBulkSheetStateBase(functionUuid, uiStateKey)

  const restoreColumnState = useCallback(() => {
    if (!gridOptions.columnApi || _.isEmpty(base.columnState)) return
    gridOptions.columnApi.applyColumnState({
      state: base.columnState,
      applyOrder: true,
    })
  }, [base.columnState, gridOptions.columnApi])

  const rememberColumnState = useCallback(() => {
    const columnState = gridOptions.columnApi?.getColumnState()
    columnState && base.saveColumnState(columnState)
  }, [base, gridOptions.columnApi])

  const restoreExpandedRows = useCallback(() => {
    if (_.isEmpty(base.expandedRowIds) || !gridOptions.api) return
    setTimeout(() => {
      const removed: string[] = []
      gridOptions.api?.forEachNode(node => {
        if (node.id && base.expandedRowIds.includes(node.id)) {
          if (node.childrenAfterGroup && node.childrenAfterGroup.length > 0) {
            node.setExpanded(true)
          } else {
            removed.push(node.id)
          }
        }
      })
      removed.length > 0 && base.collapseRows(removed)
    }, 100)
  }, [base, gridOptions.api])

  const onRowGroupOpened = useCallback(
    (e: RowGroupOpenedEvent) => {
      const key = e.node.key
      if (!key) return
      if (e.expanded) {
        base.expandRows([key])
      } else {
        base.collapseRows([key])
      }
    },
    [base]
  )

  const onFirstDataRendered = useCallback(
    e => {
      restoreExpandedRows()
      restoreColumnState()
      if (!_.isEmpty(base.filterState)) {
        gridOptions.api?.setFilterModel(base.filterState)
      }
      base.finishRestoring()
    },
    [base, gridOptions.api, restoreColumnState, restoreExpandedRows]
  )

  const onGridReady = useCallback(() => {
    restoreColumnState()
  }, [restoreColumnState])

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filter = e.api.getFilterModel()
      delete filter['uuid']
      base.saveFilterState(filter)
      return Object.keys(filter)
        .map(col => e.api.getColumnDef(col))
        .filter(v => !!v && v.field !== 'uuid') as ColDef[]
    },
    [base]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      rememberColumnState()
      const sortedCols = e.columnApi
        .getColumnState()
        .filter(col => !!col.sort)
        .map(col => e.api.getColumnDef(col.colId))
        .filter(v => !!v)
      e.api.setSuppressRowDrag(0 < sortedCols.length)
      return sortedCols
    },
    [rememberColumnState]
  )

  return {
    restoreColumnState,
    rememberColumnState,
    restoreExpandedRows,
    onRowGroupOpened,
    onFirstDataRendered,
    onGridReady,
    onFilterChanged,
    onSortChanged,
    base,
  }
}
